import type { KrLang, KrResortSlug, KrText } from './types';

/**
 * Extended Inje Speedium guide. Korea-only.
 *
 * Adds editorial copy to the racetrack page (/{lang}/racetrack/inje-speedium/),
 * which already renders the circuit data and live camera from
 * sites/kr/data/racetracks.ts. Covers the circuit, the 2026 race calendar,
 * getting there and how to pair a circuit visit with a Gangwon ski trip.
 *
 * Facts: speedium.co.kr (address, phone, hotel), Wikipedia (layout, grade,
 * series history), super-race.com (2026 calendar). Winter circuit activity was
 * not published anywhere we could check, so the copy tells people to confirm
 * rather than implying track days run in the ski season.
 */

export interface RacetrackFact {
  label: string;
  value: string;
}

export interface RacetrackEvent {
  id: string;
  name: KrText;
  /** First year the series raced here, if known. */
  since?: number;
  /** Dated rounds at Inje we could verify (ISO dates). */
  rounds?: string[];
  note?: KrText;
}

export interface RacetrackGuideSection {
  id: string;
  heading: string;
  body: string;
  bullets?: string[];
}

export interface RacetrackGuideCopy {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  badge: string;
  lede: string;
  factsHeading: string;
  facts: RacetrackFact[];
  eventsHeading: string;
  sections: RacetrackGuideSection[];
  faqHeading: string;
  faq: { q: string; a: string }[];
  linksHeading: string;
}

export interface RacetrackGuide {
  /** Matches Racetrack.slug in sites/kr/data/racetracks.ts */
  slug: 'inje-speedium';
  officialUrl: string;
  hotelUrl: string;
  phone: string;
  address: KrText;
  /** Ski resorts that pair naturally with a Speedium visit. */
  pairsWith: KrResortSlug[];
  events: RacetrackEvent[];
  links: { to: string; label: Record<KrLang, string> }[];
  copy: Record<KrLang, RacetrackGuideCopy>;
  sourceUrls: string[];
}

export const RACETRACK_GUIDE: RacetrackGuide = {
  slug: 'inje-speedium',
  officialUrl: 'https://www.speedium.co.kr/',
  hotelUrl: 'https://hc.speedium.co.kr/',
  phone: '1644-3366',
  address: {
    ko: '강원특별자치도 인제군 기린면 상하답로 130',
    en: '130 Sanghadap-ro, Girin-myeon, Inje-gun, Gangwon',
    ja: '江原特別自治道 麟蹄郡 麒麟面 上下畓路130',
    zh: '江原特别自治道麟蹄郡麒麟面上下畓路130',
  },
  pairsWith: ['vivaldi', 'yongpyong', 'alpensia'],
  events: [
    {
      id: 'superrace',
      name: { ko: '슈퍼레이스 챔피언십', en: 'Superrace Championship', ja: 'スーパーレース選手権', zh: 'Superrace锦标赛' },
      since: 2015,
      rounds: ['2026-07-18'],
      note: {
        ko: '2026 시즌 인제는 4라운드(7월 18일) 한 번만 열렸고, 나머지 라운드는 에버랜드 스피드웨이와 코리아 인터내셔널 서킷에서 열렸습니다.',
        en: 'In 2026 Inje hosted one round only — Round 4 on July 18. The other rounds ran at Everland Speedway and Korea International Circuit.',
        ja: '2026年シーズン、麟蹄で開催されたのは第4戦（7月18日）のみで、他のラウンドはエバーランド・スピードウェイと韓国インターナショナルサーキットで行われました。',
        zh: '2026赛季麟蹄只举办了第4站（7月18日），其余各站在爱宝乐园赛道和韩国国际赛车场举行。',
      },
    },
    {
      id: 'lamborghini',
      name: { ko: '람보르기니 슈퍼 트로페오 아시아', en: 'Lamborghini Super Trofeo Asia', ja: 'ランボルギーニ・スーパートロフェオ・アジア', zh: '兰博基尼Super Trofeo亚洲赛' },
      since: 2023,
    },
    {
      id: 'tcr',
      name: { ko: 'TCR 월드 투어 · TCR 아시아', en: 'TCR World Tour and TCR Asia', ja: 'TCRワールドツアー・TCRアジア', zh: 'TCR世界巡回赛与TCR亚洲赛' },
      since: 2025,
    },
    {
      id: 'hyundai-n',
      name: { ko: '현대 N 페스티벌', en: 'Hyundai N Festival', ja: 'ヒョンデNフェスティバル', zh: '现代N Festival' },
    },
  ],
  links: [
    { to: '/racetrack/inje-speedium/', label: { ko: '인제 스피디움 라이브 캠', en: 'Inje Speedium live camera', ja: '麟蹄スピーディウムのライブカメラ', zh: '麟蹄Speedium实时摄像头' } },
    { to: '/resort/vivaldi/', label: { ko: '비발디파크', en: 'Vivaldi Park', ja: 'ビバルディパーク', zh: '维瓦尔第公园' } },
    { to: '/yongpyong/', label: { ko: '용평리조트 가이드', en: 'Yongpyong guide', ja: '龍平リゾートガイド', zh: '龙平度假村指南' } },
    { to: '/winter-traffic/', label: { ko: '강원도 겨울 운전 정보', en: 'Driving in Gangwon in winter', ja: '江原道の冬の運転情報', zh: '江原道冬季驾驶' } },
    { to: '/getting-around/', label: { ko: '서울·공항에서 가는 법', en: 'Getting around from Seoul and the airports', ja: 'ソウル・空港からの交通', zh: '从首尔和机场出发的交通' } },
  ],
  copy: {
    ko: {
      title: '인제 스피디움 가이드: 서킷·경기 일정·가는 법·강원 스키와 함께',
      description: '강원도 인제의 3.908km 서킷 인제 스피디움. 코스 정보, 2026 슈퍼레이스 등 경기 일정, 서울양양고속도로로 가는 법, 서킷 호텔, 강원도 스키장과 묶어 여행하는 방법을 정리했습니다.',
      keywords: '인제 스피디움, 인제 서킷, 슈퍼레이스 인제, 인제 스피디움 호텔, 인제 스피디움 가는 법',
      h1: '인제 스피디움 완전 가이드',
      badge: '강원 · 모터스포츠',
      lede: '인제 스피디움은 서울에서 동쪽으로 약 120km, 강원도 인제군 산간에 자리한 FIA 그레이드 2 서킷입니다. 서킷과 호텔·콘도가 한곳에 모인 오토 테마파크라, 레이스 관람과 강원도 여행을 함께 계획하기 좋습니다.',
      factsHeading: '기본 정보',
      facts: [
        { label: '위치', value: '강원특별자치도 인제군 기린면 상하답로 130' },
        { label: '개장', value: '2013년 5월 25일' },
        { label: '풀 코스', value: '3.908km, 20개 코너' },
        { label: '분리 운영', value: '북쪽 코스 2.577km(12코너) / 남쪽 코스 1.375km(8코너)' },
        { label: '등급', value: 'FIA 그레이드 2' },
        { label: '숙박', value: '서킷 내 테이크 호텔 & 리조트 (호텔·콘도)' },
        { label: '대표 전화', value: '1644-3366' },
      ],
      eventsHeading: '주요 경기',
      sections: [
        {
          id: 'circuit',
          heading: '서킷 소개',
          body: '산악 지형을 살린 고저차가 큰 레이아웃이 특징입니다. 풀 코스 외에 북쪽·남쪽 코스로 나눠 쓸 수 있어 레이스와 주행 행사를 동시에 진행하기도 합니다. 슈퍼레이스, 람보르기니 슈퍼 트로페오 아시아, TCR 월드 투어와 TCR 아시아, 현대 N 페스티벌 등이 이곳에서 열렸습니다.',
        },
        {
          id: 'calendar',
          heading: '언제 가면 경기를 볼 수 있나',
          body: '국내 레이스 시즌은 봄부터 가을까지입니다. 2026 슈퍼레이스는 4월에 개막해 10월 25일 에버랜드에서 끝났고, 인제에서는 7월 18일 4라운드가 열렸습니다. 2027 일정이 발표되면 슈퍼레이스와 서킷 공식 사이트에서 확인하세요. 스키 시즌(12~3월)에 서킷 주행 행사가 열리는지는 공개된 정보가 없어 방문 전 서킷에 문의해야 합니다.',
        },
        {
          id: 'getting-there',
          heading: '가는 법',
          body: '차로 가는 것이 가장 편합니다. 서울양양고속도로(60번)가 홍천과 인제를 지나므로 서울 동쪽에서 바로 연결됩니다. 대중교통이라면 동서울터미널에서 인제 방면 시외버스를 탄 뒤 택시로 서킷까지 가야 합니다. 서킷은 읍내에서 떨어져 있으니 돌아가는 택시를 미리 확인해 두세요.',
          bullets: [
            '서울에서 약 120km',
            '서울양양고속도로 이용 (홍천·인제 경유)',
            '대중교통: 동서울터미널 → 인제 방면 시외버스 → 택시',
          ],
        },
        {
          id: 'stay',
          heading: '서킷 호텔에서 묵기',
          body: '서킷 안의 테이크 호텔 & 리조트는 4성급 호텔 객실(슈페리어·디럭스·스위트)과 콘도 객실(패밀리·키즈·반려동물 동반 등)을 함께 운영합니다. 경기 주말에는 빨리 차므로 일정이 발표되면 바로 예약하세요.',
        },
        {
          id: 'with-skiing',
          heading: '강원도 스키 여행과 묶기',
          body: '서울양양고속도로 축에 있는 홍천의 비발디파크와 함께 일정을 짜기 좋고, 영동고속도로를 이용하면 평창의 용평·알펜시아까지 이어서 갈 수 있습니다. 겨울에는 산간 도로가 얼 수 있으니 겨울용 타이어나 체인을 준비하고 출발 전 도로 정보를 확인하세요. 스키 시즌에는 경기가 없으므로, 레이스 관람이 목적이라면 여름·가을 인제 일정을 따로 잡고 겨울에는 스키에 집중하는 편이 현실적입니다.',
        },
      ],
      faqHeading: '자주 묻는 질문',
      faq: [
        { q: '인제 스피디움은 어디에 있나요?', a: '강원특별자치도 인제군 기린면 상하답로 130으로, 서울에서 동쪽으로 약 120km 떨어져 있습니다.' },
        { q: '코스 길이는 얼마인가요?', a: '풀 코스는 3.908km에 20개 코너이며, 북쪽 코스(2.577km)와 남쪽 코스(1.375km)로 나눠 쓸 수도 있습니다.' },
        { q: '겨울에도 경기가 있나요?', a: '국내 레이스는 봄부터 가을까지 열립니다. 겨울철 서킷 운영은 공개된 정보가 없어 서킷(1644-3366)에 문의하세요.' },
        { q: '서킷에 숙소가 있나요?', a: '네. 테이크 호텔 & 리조트가 호텔 객실과 콘도 객실을 운영합니다.' },
        { q: '스키장과 함께 갈 수 있나요?', a: '서울양양고속도로로 연결되는 비발디파크, 영동고속도로로 이어지는 용평·알펜시아와 함께 강원도 일정을 짤 수 있습니다.' },
      ],
      linksHeading: '관련 페이지',
    },
    en: {
      title: 'Inje Speedium Guide: Circuit, Race Calendar, Getting There',
      description: 'Inje Speedium, the 3.908 km FIA Grade 2 circuit in Gangwon: layout, 2026 Superrace and other events, the drive on the Seoul–Yangyang Expressway, the on-site hotel and combining it with a ski trip.',
      keywords: 'inje speedium, inje circuit korea, superrace inje, inje speedium hotel, how to get to inje speedium',
      h1: 'Inje Speedium — complete guide',
      badge: 'Gangwon · Motorsport',
      lede: 'Inje Speedium is an FIA Grade 2 circuit in the mountains of Inje County, Gangwon, about 120 km east of Seoul. The track, a hotel and condos sit together in one auto theme park, which makes it easy to fold a race weekend into a wider Gangwon trip.',
      factsHeading: 'Key facts',
      facts: [
        { label: 'Location', value: '130 Sanghadap-ro, Girin-myeon, Inje-gun, Gangwon' },
        { label: 'Opened', value: 'May 25, 2013' },
        { label: 'Full circuit', value: '3.908 km, 20 turns' },
        { label: 'Split layouts', value: 'North 2.577 km (12 turns) / South 1.375 km (8 turns)' },
        { label: 'Grade', value: 'FIA Grade 2' },
        { label: 'Lodging', value: 'Take Hotel & Resort on site (hotel and condo rooms)' },
        { label: 'Phone', value: '1644-3366' },
      ],
      eventsHeading: 'Major events',
      sections: [
        {
          id: 'circuit',
          heading: 'The circuit',
          body: 'The layout uses the mountain terrain, with big elevation changes. Besides the full circuit, north and south loops can run separately, so racing and driving events sometimes share the venue. Superrace, Lamborghini Super Trofeo Asia, the TCR World Tour and TCR Asia, and the Hyundai N Festival have all raced here.',
        },
        {
          id: 'calendar',
          heading: 'When to see racing',
          body: 'Korea\'s race season runs from spring to autumn. The 2026 Superrace opened in April and finished at Everland on October 25; Inje hosted Round 4 on July 18. Watch the Superrace and circuit websites for the 2027 calendar. Nothing published says whether track activity runs in the ski season (December to March), so ask the circuit before planning a winter visit around it.',
        },
        {
          id: 'getting-there',
          heading: 'Getting there',
          body: 'Driving is easiest: the Seoul–Yangyang Expressway (60) runs via Hongcheon and Inje straight from eastern Seoul. Without a car, take an intercity bus from Dong Seoul towards Inje and a taxi for the last leg — the circuit is outside town, so arrange your return taxi in advance.',
          bullets: [
            'About 120 km from Seoul',
            'Seoul–Yangyang Expressway via Hongcheon and Inje',
            'Public transport: Dong Seoul terminal → intercity bus towards Inje → taxi',
          ],
        },
        {
          id: 'stay',
          heading: 'Staying at the circuit',
          body: 'Take Hotel & Resort, inside the circuit, runs four-star hotel rooms (Superior, Deluxe, Suite) and condo rooms, including family, kids\' and pet-friendly units. Race weekends fill fast, so book once the calendar is out.',
        },
        {
          id: 'with-skiing',
          heading: 'Pairing it with a Gangwon ski trip',
          body: 'Vivaldi Park in Hongcheon sits on the same Seoul–Yangyang Expressway corridor, and the Yeongdong Expressway takes you on to Yongpyong and Alpensia in Pyeongchang. Mountain roads can ice up in winter, so carry chains or use winter tyres and check road information before setting off. Because there is no racing in the ski season, the realistic plan is a summer or autumn trip for the races and a winter trip for the snow.',
        },
      ],
      faqHeading: 'FAQ',
      faq: [
        { q: 'Where is Inje Speedium?', a: '130 Sanghadap-ro, Girin-myeon, Inje-gun in Gangwon, about 120 km east of Seoul.' },
        { q: 'How long is the track?', a: 'The full circuit is 3.908 km with 20 turns; it can also run as a 2.577 km north loop and a 1.375 km south loop.' },
        { q: 'Is there racing in winter?', a: 'Korean racing runs from spring to autumn. Winter circuit activity is not published — call the circuit on 1644-3366.' },
        { q: 'Can I stay at the circuit?', a: 'Yes. Take Hotel & Resort on site has both hotel and condo rooms.' },
        { q: 'Can I combine it with skiing?', a: 'Yes — Vivaldi Park is on the same Seoul–Yangyang Expressway corridor, and Yongpyong and Alpensia are reached via the Yeongdong Expressway.' },
      ],
      linksHeading: 'Related pages',
    },
    ja: {
      title: '麟蹄スピーディウム ガイド：サーキット・レース日程・アクセス',
      description: '江原道麟蹄の全長3.908km、FIAグレード2のサーキット「麟蹄スピーディウム」。コース、2026年スーパーレースなどのイベント、ソウル襄陽高速道路でのアクセス、併設ホテル、スキー旅行との組み合わせ方を紹介します。',
      keywords: '麟蹄スピーディウム, インジェ スピーディウム, 韓国 サーキット, スーパーレース 麟蹄',
      h1: '麟蹄（インジェ）スピーディウム 完全ガイド',
      badge: '江原道・モータースポーツ',
      lede: '麟蹄スピーディウムは、ソウルから東へ約120km、江原道麟蹄郡の山あいにあるFIAグレード2のサーキットです。サーキットとホテル・コンドミニアムが一体になったオートテーマパークなので、レース観戦を江原道旅行に組み込みやすい場所です。',
      factsHeading: '基本データ',
      facts: [
        { label: '所在地', value: '江原特別自治道 麟蹄郡 麒麟面 上下畓路130' },
        { label: '開業', value: '2013年5月25日' },
        { label: 'フルコース', value: '3.908km、20コーナー' },
        { label: '分割コース', value: '北コース2.577km（12コーナー）／南コース1.375km（8コーナー）' },
        { label: 'グレード', value: 'FIAグレード2' },
        { label: '宿泊', value: '場内のテイク・ホテル＆リゾート（ホテル・コンド）' },
        { label: '代表電話', value: '1644-3366' },
      ],
      eventsHeading: '主なイベント',
      sections: [
        {
          id: 'circuit',
          heading: 'サーキットの特徴',
          body: '山の地形を生かした高低差の大きいレイアウトが特徴です。フルコースのほか北コースと南コースに分けて使えるため、レースと走行イベントを同時に行うこともあります。スーパーレース、ランボルギーニ・スーパートロフェオ・アジア、TCRワールドツアーとTCRアジア、ヒョンデNフェスティバルなどが開催されてきました。',
        },
        {
          id: 'calendar',
          heading: 'いつ行けばレースが見られるか',
          body: '韓国のレースシーズンは春から秋までです。2026年のスーパーレースは4月に開幕し10月25日にエバーランドで閉幕、麟蹄では7月18日に第4戦が行われました。2027年の日程はスーパーレースとサーキットの公式サイトで確認してください。スキーシーズン（12〜3月）に走行イベントがあるかは公表されていないため、冬に訪れる場合はサーキットへ問い合わせが必要です。',
        },
        {
          id: 'getting-there',
          heading: 'アクセス',
          body: '車が最も便利です。ソウル襄陽高速道路（60号線）が洪川と麟蹄を通り、ソウル東部から直接つながります。公共交通なら東ソウルターミナルから麟蹄方面の市外バスに乗り、最後はタクシーで。サーキットは町から離れているので、帰りのタクシーを事前に確保しておきましょう。',
          bullets: [
            'ソウルから約120km',
            'ソウル襄陽高速道路利用（洪川・麟蹄経由）',
            '公共交通：東ソウルターミナル→麟蹄方面の市外バス→タクシー',
          ],
        },
        {
          id: 'stay',
          heading: 'サーキットのホテルに泊まる',
          body: '場内のテイク・ホテル＆リゾートは、4つ星のホテル客室（スーペリア・デラックス・スイート）とコンド客室（ファミリー、キッズ、ペット同伴など）を運営しています。レース週末はすぐ埋まるので、日程が出たら早めに予約を。',
        },
        {
          id: 'with-skiing',
          heading: '江原道のスキー旅行と組み合わせる',
          body: '同じソウル襄陽高速道路沿いにある洪川のビバルディパークと組み合わせやすく、嶺東高速道路を使えば平昌の龍平・アルペンシアまで足を延ばせます。冬は山道が凍結することがあるので、冬用タイヤやチェーンを用意し、出発前に道路情報を確認してください。スキーシーズンにはレースがないため、レース観戦は夏・秋、冬はスキーと分けて計画するのが現実的です。',
        },
      ],
      faqHeading: 'よくある質問',
      faq: [
        { q: '麟蹄スピーディウムはどこにありますか？', a: '江原特別自治道 麟蹄郡 麒麟面 上下畓路130で、ソウルから東へ約120kmです。' },
        { q: 'コースの長さは？', a: 'フルコースは3.908km・20コーナーで、北コース（2.577km）と南コース（1.375km）に分けて使うこともできます。' },
        { q: '冬もレースはありますか？', a: '韓国のレースは春から秋に行われます。冬のサーキット運営は公表されていないため、サーキット（1644-3366）に問い合わせてください。' },
        { q: 'サーキットに宿泊施設はありますか？', a: 'あります。テイク・ホテル＆リゾートがホテル客室とコンド客室を運営しています。' },
        { q: 'スキー場と組み合わせられますか？', a: 'ソウル襄陽高速道路でつながるビバルディパーク、嶺東高速道路で行ける龍平・アルペンシアと組み合わせて江原道を回れます。' },
      ],
      linksHeading: '関連ページ',
    },
    zh: {
      title: '麟蹄Speedium赛车场指南：赛道、赛程与交通',
      description: '江原道麟蹄的3.908公里FIA二级赛道麟蹄Speedium：赛道布局、2026年Superrace等赛事、经首尔—襄阳高速公路前往的方法、场内酒店，以及如何与滑雪之旅结合。',
      keywords: '麟蹄Speedium, 麟蹄赛车场, 韩国赛车场, Superrace麟蹄, 麟蹄Speedium酒店',
      h1: '麟蹄Speedium赛车场完全指南',
      badge: '江原道 · 赛车运动',
      lede: '麟蹄Speedium是一座FIA二级赛道，位于首尔以东约120公里的江原道麟蹄郡山区。赛道与酒店、公寓同处一个汽车主题公园，便于把观赛融入江原道旅行。',
      factsHeading: '基本信息',
      facts: [
        { label: '地址', value: '江原特别自治道麟蹄郡麒麟面上下畓路130' },
        { label: '开业', value: '2013年5月25日' },
        { label: '全赛道', value: '3.908公里，20个弯道' },
        { label: '分段赛道', value: '北赛道2.577公里（12弯）／南赛道1.375公里（8弯）' },
        { label: '等级', value: 'FIA二级' },
        { label: '住宿', value: '场内Take酒店及度假村（酒店客房与公寓）' },
        { label: '电话', value: '1644-3366' },
      ],
      eventsHeading: '主要赛事',
      sections: [
        {
          id: 'circuit',
          heading: '赛道介绍',
          body: '赛道依山而建，高低落差大。除全赛道外，还可拆分为北、南两条赛道独立使用，因此有时比赛与驾驶活动同时进行。Superrace、兰博基尼Super Trofeo亚洲赛、TCR世界巡回赛和TCR亚洲赛、现代N Festival等都曾在此举行。',
        },
        {
          id: 'calendar',
          heading: '什么时候能看比赛',
          body: '韩国赛车季从春季持续到秋季。2026年Superrace于4月开幕，10月25日在爱宝乐园收官，麟蹄于7月18日举办了第4站。2027年赛程请关注Superrace和赛道官网。滑雪季（12月至3月）是否有赛道活动没有公开信息，冬季前往请先向赛道咨询。',
        },
        {
          id: 'getting-there',
          heading: '交通方式',
          body: '自驾最方便：首尔—襄阳高速公路（60号）经洪川、麟蹄，从首尔东部直接相连。公共交通则从东首尔客运站乘开往麟蹄方向的长途巴士，最后一段打车。赛道远离镇区，请提前安排好返程出租车。',
          bullets: [
            '距首尔约120公里',
            '走首尔—襄阳高速公路（经洪川、麟蹄）',
            '公共交通：东首尔客运站 → 麟蹄方向长途巴士 → 出租车',
          ],
        },
        {
          id: 'stay',
          heading: '入住赛道酒店',
          body: '场内的Take酒店及度假村同时经营四星级酒店客房（高级房、豪华房、套房）和公寓客房（家庭房、儿童房、可携宠物房等）。比赛周末很快订满，赛程公布后请尽早预订。',
        },
        {
          id: 'with-skiing',
          heading: '与江原道滑雪之旅结合',
          body: '洪川的维瓦尔第公园位于同一条首尔—襄阳高速公路沿线，经岭东高速公路还可继续前往平昌的龙平和阿尔卑西亚。冬季山路可能结冰，请准备冬季轮胎或防滑链，出发前查看路况。滑雪季没有比赛，比较现实的做法是夏秋来看比赛，冬季专心滑雪。',
        },
      ],
      faqHeading: '常见问题',
      faq: [
        { q: '麟蹄Speedium在哪里？', a: '江原特别自治道麟蹄郡麒麟面上下畓路130，位于首尔以东约120公里。' },
        { q: '赛道有多长？', a: '全赛道3.908公里、20个弯道，也可拆分为2.577公里的北赛道和1.375公里的南赛道。' },
        { q: '冬天有比赛吗？', a: '韩国赛车季在春季到秋季。冬季赛道运营情况没有公开，请致电赛道1644-3366咨询。' },
        { q: '赛道有住宿吗？', a: '有。Take酒店及度假村提供酒店客房和公寓客房。' },
        { q: '能和滑雪一起安排吗？', a: '可以。维瓦尔第公园在同一条首尔—襄阳高速公路沿线，龙平和阿尔卑西亚可经岭东高速公路前往。' },
      ],
      linksHeading: '相关页面',
    },
  },
  sourceUrls: [
    'https://www.speedium.co.kr/',
    'https://hc.speedium.co.kr/',
    'https://en.wikipedia.org/wiki/Inje_Speedium',
    'https://www.super-race.com/',
    'https://en.wikipedia.org/wiki/Expressways_in_South_Korea',
  ],
};
